import { useEffect, useState, useMemo, useRef } from 'react';
import {
  useCountry,
  useSettings,
  useLocalizedVariant,
  useCartAddItem,
  useProductInventoryByHandle,
  useRouter,
} from '@backpackjs/storefront';
import DarkCircleCheckmark from 'src/snippets/icons/DarkCircleCheckmark';
import { YotpoScript } from '@/utils/YotpoScript';

import AfterPay from '@/snippets/products/AfterPay';
import ProductGallery from '@/snippets/products/ProductGallery';
import ProductInformation from '@/snippets/products/ProductInformation';
import BackInStockModal from '@/snippets/products/BackInStockModal';
import BuyWithPrime from '@/snippets/products/BuyWithPrime';
import BuyWithPrimeReviews from '@/snippets/products/BuyWithPrimeReviews';
import BuyWithPrimeReviewsBadge from '@/snippets/products/BuyWithPrimeReviewsBadge';
import { getMetafieldMap } from '@/utils/Metafields';
import BackToTop from '@/snippets/components/BackToTop';
import ProductSticky from '@/snippets/products/ProductSticky';
import { useDataLayerActions } from '@hooks';
import ProductEmailSignup from '@/snippets/globals/layout/ProductEmailSignup';
import BeamProductDetailWidget from '@/snippets/beam/BeamProductDetailWidget';

export default function ProductTemplate({ renderSections, product }) {
  const settings = useSettings();
  const { sendViewProductEvent } = useDataLayerActions();
  const { country } = useCountry();
  const countryCode = country?.isoCode || 'US';
  const isUs = countryCode === 'US';
  const { cartAddItem } = useCartAddItem();
  const variant = useGetInitialVariant(product);
  const { inventory, ...inventoryStatus } = useProductInventoryByHandle({
    handle: product.handle,
  });
  const metafieldMap = useMemo(() => {
    return getMetafieldMap(product);
  }, [product?.id]);
  const { localized: localizedVariant } = useLocalizedVariant({ variant });
  const selectedVariant = { ...variant, ...localizedVariant };
  const [selectedPlanId, setSelectedPlanId] = useState(
    selectedVariant?.sellingPlanAllocations?.[0]?.sellingPlan?.id || null
  );

  const firstUpsellProduct = metafieldMap.product_upsell_1;
  const buyboxRef = useRef(null);
  const productInfoRef = useRef(null);
  const isComingSoon = metafieldMap.coming_soon;
  const router = useRouter();

  if (
    typeof history !== 'undefined' &&
    typeof history.scrollRestoration !== 'undefined'
  ) {
    history.scrollRestoration = 'manual';
  }

  const selectedPlan = selectedVariant?.sellingPlanAllocations?.find(
    (plan) => plan?.sellingPlan.id === selectedPlanId
  );

  const firstPlan = selectedVariant?.sellingPlanAllocations?.[0];

  const planAmount = parseFloat(
    selectedPlan?.priceAdjustments?.[0]?.price?.amount ||
      firstPlan?.priceAdjustments?.[0]?.price?.amount
  );
  const variantAmount = selectedVariant?.priceV2?.amount;
  const compareAmount = selectedVariant?.compareAtPriceV2?.amount;

  const isLocalized = !!selectedVariant?.priceV2?.locale;
  const currencySymbol = isLocalized ? country?.currency?.symbol || '$' : '$';
  const currencyCode = selectedVariant?.priceV2?.currencyCode || 'USD';

  const sellingPlanPrice = planAmount
    ? `${currencySymbol}${planAmount.toFixed(2)}`.replace('.00', '')
    : null;
  const comparePrice = compareAmount
    ? `${currencySymbol}${compareAmount}`.replace('.00', '')
    : null;
  const variantPrice = `${currencySymbol}${variantAmount}`.replace('.00', '');

  const planOptions = selectedVariant?.sellingPlanAllocations?.map((plan) => ({
    label: plan?.sellingPlan?.options?.[0]?.value,
    value: plan?.sellingPlan?.id,
  }));

  useEffect(() => {
    if (metafieldMap.display_custom_cursor === 'true') {
      document.body.classList.add('show-custom-cursor');
    } else {
      document.body.classList.remove('show-custom-cursor');
    }
  }, [])

  useEffect(() => {
    if (!selectedVariant) return;
    const { search } = window.location;
    const searchParams = new URLSearchParams(search);
    let planId = searchParams.get('selling_plan');
    const defaultSubscription = searchParams.has('subscription');
    let activePlan = selectedVariant.sellingPlanAllocations?.find((plan) => {
      return plan.sellingPlan.id.includes(planId);
    });

    if (!activePlan && defaultSubscription) {
      activePlan = selectedVariant.sellingPlanAllocations?.[0].sellingPlan;
      planId = selectedVariant.sellingPlanAllocations?.[0].sellingPlan.id;

      delete router.query.subscription;

      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
          },
        },
        undefined,
        { shallow: true }
      );
    }

    if (activePlan) {
      setSelectedPlanId(planId);
    }
  }, [selectedVariant]);

  const [productsizes, productupsells, videowise, sections] = useMemo(() => {
    const _sections = renderSections();
    if (!_sections) return [null, []];

    const productSizesSection = _sections.filter(
      (s) => s.props['data-comp'] === 'productsizes'
    );
    const productUpsellsSection = _sections.filter(
      (s) => s.props['data-comp'] === 'productupsells'
    );
    const videoWiseSection = _sections.filter(
      (s) => s.props['data-comp'] === 'VideoWise'
    );
    const otherSections = _sections.filter(
      (s) => !['productsizes'].includes(s.props['data-comp'])
    );
    return [productSizesSection, productUpsellsSection, videoWiseSection, otherSections];
  }, [renderSections]);

  const tracksInventory = product?.tracksInventory;
  const hasInventory = !tracksInventory || (inventory?.totalInventory || 0) > 0;

  const toggleBackInStockModal = (e) => {
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('backInStockModal:open'));
  };

  const variantIsAvailable =
    (inventory?.variants?.[selectedVariant?.id]?.availableForSale &&
      hasInventory) ||
    !tracksInventory;

  const jsonSchema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.title,
    image: product.images.map((image) => image.src),
    description: product.description,
    brand: {
      '@type': 'Brand',
      name: product.vendor.replace('_', ' '),
    },
    offers: product.variants.map((variant) => {
      return {
        '@type': 'Offer',
        url: product.onlineStoreUrl,
        priceCurrency: variant.currencyCode,
        price: variant.price,
        itemCondition: 'https://schema.org/NewCondition',
        availability: `https://schema.org/${
          variant.availableForSale ? 'InStock' : 'OutOfStock'
        }`,
      };
    }),
  };
  useMemo(() => {
    YotpoScript();
  }, [product.id]);

  useEffect(() => {
    if (!variant) return;
    sendViewProductEvent({ product, selectedVariant: variant });
  }, [variant?.product?.handle, sendViewProductEvent]);

  return (
    <div
      data-comp="ProductTemplate"
      id={product?.legacyResourceId}
      key={product?.legacyResourceId}
    >
      <div
        className="product-main-container flex w-full flex-col lg:container lg:mx-auto lg:flex-row"
        ref={buyboxRef}
      >
        <div className="product-gallery lg:z-[1999] flex w-full flex-shrink-0 lg:w-7/12 mb-4 lg:mb-0 lg:sticky lg:h-[100%] lg:top-[80px]">
          <ProductGallery key={product.legacyResourceId} product={product} />
        </div>

        <div
          className="product-info-container m-auto w-full lg:m-0 lg:w-5/12"
          ref={productInfoRef}
        >
          <div className="product-info-inner-container m-auto max-w-[642px] p-6 pt-1 lg:m-0 lg:p-12 lg:pt-6 lg:pr-8 xl:pr-12 lg:pb-0">
            <ProductInformation
              product={product}
              selectedVariant={selectedVariant}
              selectedPlan={selectedPlan}
            />
            {productsizes}

            <div className="block w-full">
              <ProductOptions
                product={product}
                activeVariant={selectedVariant}
              />
              {variantIsAvailable &&
                country &&
                isUs &&
                selectedVariant?.sellingPlanAllocations &&
                selectedVariant?.sellingPlanAllocations?.length > 0 && (
                  <div className="mb-[16px] overflow-hidden border">
                    <button
                      type="button"
                      className={`one-time-option grid w-full grid-cols-[auto_auto_1fr] border-b px-[12px] py-[15px] md:px-[20px] bg-[#F5F5F5]  ${
                        !selectedPlanId && 'bg-[#FFFFFF]'
                      }`}
                      onClick={() => {
                        setSelectedPlanId(null);
                        window.dataLayer.push({
                          'event': 'PDP - One time select'
                        });
                      }}
                    >
                      <div className="relative top-[2px] mr-[10px] flex h-[18px] w-[18px] cursor-pointer rounded-full border md:top-0 md:mr-[12px] md:h-[20px] md:w-[20px]">
                        {!selectedPlanId && (
                          <div className="relative top-[2px] left-[2px] h-[12px] w-[12px] rounded-full bg-grey-1 md:h-[14px] md:w-[14px]" />
                        )}
                      </div>
                      <p className="mb-0 font-gothamBold text-[14px]">
                        One-time purchase
                      </p>
                      <div className="ml-auto font-gothamBold text-[14px]">
                        {comparePrice && (
                          <span className="product-original-price mr-2 font-gothamBold text-[14px] text-[#757575] line-through">
                            {`${comparePrice}`}
                          </span>
                        )}
                        <span>{`${variantPrice}`}</span>
                      </div>
                    </button>
                    <div className={`${selectedPlanId ? 'bg-[#FFFFFF]' : 'bg-[#F5F5F5]'}`}>
                      <button
                        type="button"
                        className="subscribe-and-save-option grid w-full grid-cols-[auto_auto_1fr] px-[12px] py-[15px] md:px-[20px]"
                        onClick={() => {
                          setSelectedPlanId(firstPlan?.sellingPlan?.id);
                          window.dataLayer.push({
                            'event': 'PDP - Subscribe & Save select'
                          });
                        }}
                      >
                        <span className="relative top-[2px] mr-[10px] flex h-[18px] w-[18px] cursor-pointer rounded-full border md:top-0 md:mr-[12px] md:h-[20px] md:w-[20px]">
                          {selectedPlanId && (
                            <div className="relative top-[2px] left-[2px] h-[12px] w-[12px] rounded-full bg-grey-1 md:h-[14px] md:w-[14px]" />
                          )}
                        </span>
                        <div className='text-left'>
                          <p className="mb-0 font-gothamBold text-[14px]">
                            <span className="whitespace-nowrap">
                              {`Subscribe & save ${firstPlan?.sellingPlan?.priceAdjustments[0].adjustmentValue?.adjustmentPercentage}%`}
                            </span>
                            <span className="desktop-free-shipping-text ml-[20px] hidden whitespace-nowrap bg-[#E61A4F] px-[7px] py-[4px] text-[11px] text-white xl:inline">
                              {settings?.subscriptions?.freeShippingText ||
                                'Free Shipping'}
                            </span>
                          </p>
                          <span className="mobile-free-shipping-text whitespace-nowrap bg-[#E61A4F] px-[7px] py-[4px] font-gothamBold text-[11px] text-white xl:hidden">
                            {settings?.subscriptions?.freeShippingText ||
                              'Free Shipping'}
                          </span>
                        </div>
                        <div className="ml-auto">
                          <span className="product-original-price ml-4 font-gothamBold text-[14px] text-[#757575] line-through">
                            {`${settings?.discountHighlight?.enableDiscountHighlight ? comparePrice || variantPrice : variantPrice}`}
                          </span>
                          <span className="ml-2 font-gothamBold text-[14px]">
                            {`${sellingPlanPrice}`}
                          </span>
                        </div>
                        <p className='new-pdp-descriptor col-span-2 col-start-2 mb-0 text-[10px] text-left hidden'>
                          <span className='bg-primary-2 px-[7px] py-1 font-gothamBold text-white mr-2'>
                            {settings?.subscriptions?.colorBadgeText || 'Most Popular!'}
                          </span>
                          {firstPlan?.sellingPlan?.priceAdjustments[0].adjustmentValue?.adjustmentPercentage}% off + Free Shipping
                        </p>
                      </button>
                      {selectedPlanId && (
                        <div className="px-[12px] pb-[15px] md:px-[20px]">
                          <div className="mb-[8px] flex items-end whitespace-nowrap">
                            <span className="text-[12px] text-black">
                              Ships every:
                            </span>
                            <div className="select-wrap-2 relative flex flex-col">
                              <select
                                className="ml-2 cursor-pointer border-0 border-b bg-transparent px-2 pb-0 pr-4 pt-0 font-gothamBold text-[12px] text-black text-black placeholder-black outline-0 hover:placeholder-black focus:placeholder-black"
                                name="selling-plan-select"
                                id="selling-plan-select"
                                value={selectedPlanId}
                                onChange={(e) => {
                                  setSelectedPlanId(e.target.value);
                                }}
                              >
                                {planOptions.map((option, optionIndex) => (
                                  <option
                                    key={`option-selling-plan-select-${optionIndex}`}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="mt-[10px] flex items-baseline whitespace-nowrap">
                            <span className="text-[10px] text-black ">
                              {settings?.subscriptions?.cancelText ||
                                'Cancel anytime + membership perks.'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>

            <div className="block w-full">
              <div className="min-h-[48px] lg:min-h-[52px]">
                {variantIsAvailable && (
                  <button
                    type="button"
                    className="button button--checkout block flex w-full justify-center md:px-4 md:text-[12px] lg:text-[16px] h-[70px] flex items-center"
                    onClick={async () => {
                      try {
                        // custom attribute needed for Pack to properly serparate line items in the cart
                        const addItem =
                          selectedPlanId && isUs
                            ? {
                                merchandiseId: selectedVariant.id,
                                sellingPlanId: btoa(selectedPlanId),
                                quantity: 1,
                                attributes: [
                                  {
                                    key: '_sellingPlan',
                                    value: selectedPlanId,
                                  },
                                ],
                              }
                            : {
                                merchandiseId: selectedVariant.id,
                                quantity: 1,
                                attributes: [],
                              };
                        if (firstUpsellProduct) {
                          addItem.attributes.push({
                            key: '_upsellProduct',
                            value: firstUpsellProduct,
                          });
                        }
                        await cartAddItem(addItem, () => {
                          window.dispatchEvent(new CustomEvent('openSideCart'));
                          if (selectedPlanId && isUs) {
                            window.dataLayer.push({
                              'event': 'PDP - Add_to_cart Subscribe & Save'
                            });
                          } else {
                            window.dataLayer.push({
                              'event': 'PDP - Add_to_cart One Time'});
                          }
                        });
                      } catch (error) {
                        console.error(error);
                        alert(
                          'Something whent wrong while adding to cart, please try again.'
                        );
                      }
                    }}
                  >
                    <span className="font-gotham">add to cart</span>
                    {country && comparePrice && (
                      <div>
                        <span className="product-original-price ml-4 font-gothamBold line-through">
                          {comparePrice}
                        </span>
                        {selectedPlan ? (
                          <span className="ml-2 font-gothamBold">
                            {`${sellingPlanPrice}`}
                          </span>
                        ) : (
                          <span className="ml-2 font-gothamBold">
                            {variantPrice}
                          </span>
                        )}
                      </div>
                    )}
                    {country &&
                      selectedPlan &&
                      sellingPlanPrice &&
                      !comparePrice && (
                        <div>
                          <span className="product-original-price ml-4 font-gothamBold line-through">
                            {variantPrice}
                          </span>
                          <span className="ml-2 font-gothamBold">
                            {`${sellingPlanPrice}`}
                          </span>
                        </div>
                      )}
                    {country && !comparePrice && !selectedPlan && (
                      <span className="ml-8 font-gothamBold">
                        {variantPrice}
                      </span>
                    )}
                  </button>
                )}
                {inventoryStatus.success && !variantIsAvailable && (
                  <div>
                    {isComingSoon !== 'true' ? (
                      <button
                    type="button"
                    disabled
                    className="button button--primary block flex w-full justify-center"
                  >
                      <span className="font-gotham">
                        {hasInventory ? 'unavailable' : 'out of stock'}
                      </span>
                      </button>
                    ) : (
                      <div>
                        <div className="button block flex w-full justify-center border-grey-7 bg-grey-7 text-black">
                          <span className="font-gotham">Coming Soon</span>
                        </div>
                        <ProductEmailSignup />
                      </div>
                    )}
                  </div>
                )}
                {isComingSoon !== 'true' &&
                  inventoryStatus.success &&
                  !hasInventory && (
                    <button
                      type="button"
                      className="button button--primary mt-5 block flex w-full justify-center"
                      onClick={toggleBackInStockModal}
                    >
                      <span className="font-gotham">
                        email me when available
                      </span>
                    </button>
                  )}
              </div>
              {country && isUs && inventoryStatus.success && variantIsAvailable && isComingSoon !== 'true' && (
                <AfterPay
                  variantId={selectedVariant?.id}
                  price={variantPrice || 0}
                  currency={currencyCode}
                />
              )}
            </div>
            {country && isUs && (
              <div className="mt-8 !hidden md:!flex lg:flex-row">
                <div className="productValueProp flex w-1/3">
                  {settings?.product?.productValueProps?.value_icon_1 && (
                    <span className="mr-4 lg:mr-2 relative w-[50px] h-[50px]">
                      <img
                        className="md:w-[33px] xl:w-[auto]"
                        src={settings?.product?.productValueProps?.value_icon_1?.src}
                        alt={settings?.product?.productValueProps?.value_text_1 || 'free shipping on all subscriptions'}
                      />
                    </span>
                  )}
                    <p className="text-small">
                      {settings?.product?.productValueProps?.value_text_1 || 'free shipping on all subscriptions'}
                    </p>
                </div>
                <div className="productValueProp flex lg:ml-2 w-1/3">
                  {settings?.product?.productValueProps?.value_icon_2 && (
                    <span className="mr-4 lg:mr-2 xl:mr-0 relative w-[50px] h-[50px]">
                      <img
                        className="md:w-[33px] xl:w-[auto]"
                        src={settings?.product?.productValueProps?.value_icon_2?.src}
                        alt={settings?.product?.productValueProps?.value_text_2 || 'no hassle 30-day returns'}
                      />
                    </span>
                  )}
                    <p className="text-small">
                      {settings?.product?.productValueProps?.value_text_2 || 'no hassle 30-day returns'}
                    </p>
                </div>
                <div className="productValueProp flex lg:ml-4 w-1/3">
                  {settings?.product?.productValueProps?.value_icon_3 && (
                    <span className="mr-4 lg:mr-2 relative w-[50px] h-[50px]">
                      <img
                        className="md:w-[33px] xl:w-[auto]"
                        src={settings?.product?.productValueProps?.value_icon_3?.src}
                        alt={settings?.product?.productValueProps?.value_text_3 || 'rewards with every purchase'}
                      />
                    </span>
                  )}
                    <p className="text-small">
                      {settings?.product?.productValueProps?.value_text_3 || 'rewards with every purchase'}
                    </p>
                </div>
              </div>
            )}
            {isUs && (
              <BuyWithPrime
                key={`${selectedVariant?.sku}-bwp-widget`}
                sku={selectedVariant?.sku}
              />
            )}
            {isUs && (
              <BuyWithPrimeReviewsBadge
                key={`${selectedVariant?.sku}-bwp-ratings`}
                sku={selectedVariant?.sku}
              />
            )}
            <BeamProductDetailWidget />
            <div className='pdp-buybox-video-wise'>
              {videowise}
            </div>
            <div className="pdp-buybox-desktop-upsells hidden lg:block">
              {productupsells}
            </div>
          </div>
        </div>
      </div>
      <BackToTop scrollReference={buyboxRef} />
      {sections}
      <p className="mt-[44px] mb-[10px] text-center font-tiempos text-h4-m md:mt-[100px] md:mb-[40px] md:text-h4">
        Product Reviews &amp; Questions
      </p>

      <div
        key={product?.legacyResourceId}
        id="pdp-yotpo-reviews-widget"
        className="yotpo yotpo-main-widget !mb-[44px] md:!mb-[80px]"
        data-product-id={product?.legacyResourceId}
        data-price={`$${selectedVariant?.price}`}
        data-currency="USD"
        data-name={product?.title}
        data-url={product?.url}
        data-image-url={product?.images[0]?.src}
      />
      {isUs && (
        <BuyWithPrimeReviews
          key={`${selectedVariant?.sku}-bwp-reviews`}
          sku={selectedVariant?.sku}
        />
      )}
      <BackInStockModal selectedVariant={selectedVariant} product={product} />

      {inventoryStatus.success && variantIsAvailable && isComingSoon !== 'true' && (
        <ProductSticky
          scrollReference={productInfoRef}
          product={product}
          variantPrice={variantPrice}
          comparePrice={comparePrice}
        />
      )}

      <script
          type="application/ld+json"
          className="product-schema"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonSchema) }}
        />
    </div>
  );
}

const useGetInitialVariant = (product) => {
  const router = useRouter();

  if (!product) return null;

  const legacyVariantIdQuery = router.query.variant;

  if (legacyVariantIdQuery) {
    //! Attempt to find whaproduct variant that matches the query param
    const queriedVariant = product.variants.find(
      ({ legacyResourceId }) => legacyResourceId === legacyVariantIdQuery
    );

    if (queriedVariant) {
      return queriedVariant;
    }

    return product.variants[0];
  }
  //! We will find the first avaiable, and set that as the variant
  const firstAvailableVariant = product.variants.find(
    ({ availableForSale }) => availableForSale
  );

  if (firstAvailableVariant) {
    return firstAvailableVariant;
  }
  if (product?.variants?.length) {
    return product.variants[0];
  }
};

function ProductOptions({ activeVariant, product }) {
  const router = useRouter();

  if (!product || !product.options || product.variants.length <= 1) {
    return null;
  }

  const [selectedOptions, setSelectedOptions] = useState(
    activeVariant?.title.split(' / ').map((value) => value.trim())
  );

  useEffect(() => {
    if (selectedOptions) {
      const variantTitle = selectedOptions.join(' / ');

      const selectedVariant = product.variants.find(
        (variant) => variant.title === variantTitle
      );

      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            variant: selectedVariant.legacyResourceId,
          },
        },
        undefined,
        { shallow: true }
      );
    }
    if (
      typeof window !== 'undefined' &&
      window.hasOwnProperty('updateYotpoWidgets')
    ) {
      updateYotpoWidgets();
    }
  }, [selectedOptions]);

  return product.options.map((option, nameIndex) => {
    return (
      <div key={nameIndex} className="mb-6">
        <span className="text-small mb-1 font-gothamBold text-black">
          {' '}
          {option.name}{' '}
        </span>

        <div className="mx-auto flex w-full">
          {option.values.map((value, valueIndex) => {
            return (
              <button
                type="button"
                key={valueIndex}
                className={`w-1/6 border border py-2 px-1 font-gothamBold text-[12px] duration-200 hover:border-black hover:bg-grey-9 hover:text-black lg:text-base xl:text-[14px] ${
                  value === selectedOptions[nameIndex]
                    ? 'border-black bg-grey-9 text-black'
                    : 'border-grey-4 text-grey-3'
                }`}
                onClick={() => {
                  setSelectedOptions((curr) => {
                    const selectedOptions = [...curr];
                    selectedOptions[nameIndex] = value.trim();
                    return selectedOptions;
                  });
                }}
              >
                {value}
              </button>
            );
          })}
        </div>
      </div>
    );
  });
}